import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronRight, ChevronLeft, Check, Search, X, ArrowUp, ArrowDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const PRI_COLORS = {
  1: { bg: '#ef444420', text: '#ef4444', label: 'P1 (Critical)' },
  2: { bg: '#f59e0b20', text: '#f59e0b', label: 'P2 (High)' },
  3: { bg: '#3b82f620', text: '#3b82f6', label: 'P3 (Medium)' },
  4: { bg: '#143A6B20', text: '#143A6B', label: 'P4 (Low)' },
  5: { bg: '#94a3b820', text: '#94a3b8', label: 'P5' },
};

function fmtNum(n) { return n != null ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n) : '-'; }

export default function SWMPriorityWizard({ projectId, onClose }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [assignments, setAssignments] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [bulkPriority, setBulkPriority] = useState(2);
  const [areaPriority, setAreaPriority] = useState({ area: '', structureType: '', priority: 3 });
  const [overrideForm, setOverrideForm] = useState({ structuralTag: '', priority: 1, reason: '', docRef: '', urgency: 'NORMAL' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/priority/wizard/prepare`);
      setData(res.data);
      const init = {};
      for (const t of (res.data?.tags || [])) {
        if (t.priority != null) {
          init[t.structuralTag] = { priority: t.priority, subPriority: t.subPriority || 1 };
        }
      }
      setAssignments(init);
    } catch { toast.error('Failed to load wizard data'); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const allTags = data?.tags || [];
  const areas = [...new Set(allTags.map(t => t.area).filter(Boolean))].sort();
  const stypes = [...new Set(allTags.map(t => t.structureType).filter(Boolean))].sort();

  const getEffPri = (tagId) => assignments[tagId]?.priority ?? allTags.find(t => t.structuralTag === tagId)?.priority;
  const getEffSub = (tagId) => assignments[tagId]?.subPriority ?? allTags.find(t => t.structuralTag === tagId)?.subPriority;

  const setTagPriority = (tagId, priority, subPriority) => {
    setAssignments(prev => ({ ...prev, [tagId]: { priority, subPriority: subPriority || 1 } }));
  };

  const assignBulk = () => {
    const updated = { ...assignments };
    selected.forEach(tid => { updated[tid] = { priority: bulkPriority, subPriority: 1 }; });
    setAssignments(updated);
    setSelected(new Set());
    toast.success(`Assigned P${bulkPriority} to ${selected.size} tags`);
  };

  const assignArea = () => {
    if (!areaPriority.area) return;
    const matching = allTags.filter(t => t.area === areaPriority.area && t.priority == null && (!areaPriority.structureType || t.structureType === areaPriority.structureType));
    const updated = { ...assignments };
    matching.forEach(t => { updated[t.structuralTag] = { priority: areaPriority.priority, subPriority: 1 }; });
    setAssignments(updated);
    toast.success(`Assigned P${areaPriority.priority} to ${matching.length} tags in ${areaPriority.area}`);
  };

  const autoSubPriority = () => {
    const updated = { ...assignments };
    for (let p = 1; p <= 5; p++) {
      const pTags = allTags.filter(t => (updated[t.structuralTag]?.priority || t.priority) === p)
        .sort((a, b) => (b.totalMTOWeight || 0) - (a.totalMTOWeight || 0));
      pTags.forEach((t, idx) => {
        if (updated[t.structuralTag]) updated[t.structuralTag].subPriority = idx + 1;
        else updated[t.structuralTag] = { priority: p, subPriority: idx + 1 };
      });
    }
    setAssignments(updated);
    toast.success('Sub-priorities auto-assigned by weight (heaviest first)');
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/priority/wizard/impact-preview`);
      setPreview(res.data);
    } catch { toast.error('Failed to load preview'); }
    setPreviewLoading(false);
  };

  useEffect(() => { if (step === 4) loadPreview(); }, [step]);

  const applyAll = async () => {
    setApplying(true);
    try {
      const assignList = Object.entries(assignments).map(([tag, val]) => ({
        structuralTag: tag, priority: val.priority, subPriority: val.subPriority,
      }));
      const res = await api.post(`/v1/projects/${projectId}/swm/priority/wizard/finalize`, {
        assignments: assignList, triggerAllocationImmediately: true,
      });
      toast.success(`Applied ${res.data?.assignmentsCreated || 0} assignments and triggered allocation`);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to apply'); }
    setApplying(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const stepLabels = ['Review Tags', 'Assign Priorities', 'Sub-Priority', 'Impact Preview', 'Confirm & Apply'];

  const priorityDist = {};
  for (let p = 1; p <= 5; p++) {
    const pts = allTags.filter(t => (assignments[t.structuralTag]?.priority ?? t.priority) === p);
    priorityDist[p] = { count: pts.length, weight: pts.reduce((s, t) => s + (t.totalMTOWeight || 0), 0) };
  }
  const unassigned = allTags.filter(t => !assignments[t.structuralTag] && t.priority == null);
  const chartData = Object.entries(priorityDist).map(([p, v]) => ({ name: `P${p}`, count: v.count, weight: Math.round(v.weight), fill: PRI_COLORS[p]?.text || '#94a3b8' }));
  chartData.push({ name: 'None', count: unassigned.length, weight: Math.round(unassigned.reduce((s, t) => s + (t.totalMTOWeight || 0), 0)), fill: '#6b7280' });

  const filteredTags = allTags.filter(t => {
    if (!search) return true;
    return t.structuralTag?.toLowerCase().includes(search.toLowerCase()) || t.structureNameRef?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-[90%] max-w-[1000px] max-h-[85vh] rounded-xl border overflow-hidden flex flex-col" style={{ background: 'var(--card-bg)', borderColor: '#F5920640' }} data-testid="swm-priority-wizard">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ background: '#F5920608', borderColor: '#F5920620' }}>
          <h3 className="text-sm font-bold" style={{ color: '#F59206' }}>Priority Assignment Wizard</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-red-500/10"><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-1 px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {stepLabels.map((label, idx) => {
            const sNum = idx + 1;
            const isActive = step === sNum;
            const isDone = step > sNum;
            return (
              <div key={sNum} className="flex items-center gap-1" data-testid={`wizard-step-${sNum}`}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold"
                  style={{ background: isDone ? '#22c55e' : isActive ? '#F59206' : 'var(--card-bg-alt)', color: isDone || isActive ? '#fff' : 'var(--text-muted)' }}>
                  {isDone ? <Check className="w-3 h-3" /> : sNum}
                </div>
                <span className="text-[10px] font-medium hidden md:inline" style={{ color: isActive ? '#F59206' : 'var(--text-muted)' }}>{label}</span>
                {idx < 4 && <ChevronRight className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />}
              </div>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* STEP 1: Review Tags */}
          {step === 1 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div className="p-2 rounded border text-center" style={{ background: 'var(--card-bg-alt)', borderColor: 'var(--border-color)' }}>
                  <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Total Tags</p>
                  <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{data?.totalTags || 0}</p>
                </div>
                <div className="p-2 rounded border text-center" style={{ background: '#22c55e08', borderColor: '#22c55e30' }}>
                  <p className="text-[9px]" style={{ color: '#22c55e' }}>Assigned</p>
                  <p className="text-xl font-bold" style={{ color: '#22c55e' }}>{data?.assignedCount || 0}</p>
                </div>
                <div className="p-2 rounded border text-center" style={{ background: '#ef444408', borderColor: '#ef444430' }}>
                  <p className="text-[9px]" style={{ color: '#ef4444' }}>Unassigned</p>
                  <p className="text-xl font-bold" style={{ color: '#ef4444' }}>{data?.unassignedCount || 0}</p>
                </div>
              </div>
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 10 }} />
                    <Bar dataKey="count" name="Tags" radius={[4, 4, 0, 0]}>{chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* STEP 2: Assign Priorities */}
          {step === 2 && (
            <div className="space-y-3">
              {/* Bulk assign */}
              <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg border" style={{ background: 'var(--card-bg-alt)', borderColor: 'var(--border-color)' }}>
                <span className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Bulk Assign Selected:</span>
                <select value={bulkPriority} onChange={e => setBulkPriority(Number(e.target.value))} className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  {[1,2,3,4,5].map(p => <option key={p} value={p}>P{p} - {PRI_COLORS[p].label}</option>)}
                </select>
                <Button size="sm" onClick={assignBulk} disabled={selected.size === 0} className="text-[10px] h-6 px-2" data-testid="wizard-bulk-assign">
                  Apply to {selected.size} tags
                </Button>
              </div>
              {/* Area assign */}
              <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg border" style={{ background: 'var(--card-bg-alt)', borderColor: 'var(--border-color)' }}>
                <span className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Area Assign:</span>
                <select value={areaPriority.area} onChange={e => setAreaPriority(p => ({ ...p, area: e.target.value }))} className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="">Select Area</option>
                  {areas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <select value={areaPriority.structureType} onChange={e => setAreaPriority(p => ({ ...p, structureType: e.target.value }))} className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="">All Types</option>
                  {stypes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={areaPriority.priority} onChange={e => setAreaPriority(p => ({ ...p, priority: Number(e.target.value) }))} className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  {[1,2,3,4,5].map(p => <option key={p} value={p}>P{p}</option>)}
                </select>
                <Button size="sm" onClick={assignArea} className="text-[10px] h-6 px-2" data-testid="wizard-area-assign">Assign Area</Button>
              </div>
              {/* Search */}
              <div className="flex items-center gap-1 px-2 py-1 rounded border max-w-xs" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)' }}>
                <Search className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tags..." className="bg-transparent border-none outline-none text-[11px] w-full" style={{ color: 'var(--text-primary)' }} data-testid="wizard-search" />
              </div>
              {/* Tag table */}
              <div className="rounded-lg border overflow-hidden max-h-[300px] overflow-y-auto" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <table className="w-full text-[10px]">
                  <thead className="sticky top-0 z-10">
                    <tr style={{ background: 'var(--card-bg-alt)' }}>
                      <th className="w-6 px-1"><input type="checkbox" onChange={e => { if (e.target.checked) setSelected(new Set(filteredTags.map(t => t.structuralTag))); else setSelected(new Set()); }} /></th>
                      {['Tag #', 'Structure', 'Area', 'Type', 'MTO Wt', 'Priority'].map(h => (
                        <th key={h} className="px-2 py-1.5 text-left font-semibold" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTags.map(t => {
                      const pri = getEffPri(t.structuralTag);
                      const pc = pri ? PRI_COLORS[pri] : null;
                      return (
                        <tr key={t.structuralTag} style={{ borderBottom: '1px solid var(--border-color)' }} className="hover:brightness-95">
                          <td className="px-1 text-center"><input type="checkbox" checked={selected.has(t.structuralTag)} onChange={e => { const s = new Set(selected); if (e.target.checked) s.add(t.structuralTag); else s.delete(t.structuralTag); setSelected(s); }} /></td>
                          <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{t.structuralTag}</td>
                          <td className="px-2 py-1" style={{ color: 'var(--text-muted)' }}>{t.structureNameRef}</td>
                          <td className="px-2 py-1" style={{ color: 'var(--text-muted)' }}>{t.area}</td>
                          <td className="px-2 py-1" style={{ color: 'var(--text-muted)' }}>{t.structureType}</td>
                          <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(t.totalMTOWeight)} kg</td>
                          <td className="px-2 py-1">
                            <select value={pri || ''} onChange={e => setTagPriority(t.structuralTag, Number(e.target.value) || null, 1)} className="text-[9px] px-1 py-0.5 rounded border" style={{ background: pc ? pc.bg : 'var(--input-bg)', borderColor: pc ? pc.text : 'var(--border-color)', color: pc ? pc.text : 'var(--text-muted)' }}>
                              <option value="">--</option>
                              {[1,2,3,4,5].map(p => <option key={p} value={p}>P{p}</option>)}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 3: Sub-Priority */}
          {step === 3 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Sub-priority determines allocation order within the same priority level. Lower number = allocated first.</p>
                <Button size="sm" onClick={autoSubPriority} className="text-[10px] h-6 px-2" data-testid="wizard-auto-subpri">
                  <ArrowDown className="w-3 h-3 mr-1" /> Auto-assign by Weight
                </Button>
              </div>
              {[1,2,3,4,5].map(p => {
                const pTags = allTags.filter(t => (assignments[t.structuralTag]?.priority ?? t.priority) === p)
                  .sort((a, b) => (getEffSub(a.structuralTag) || 999) - (getEffSub(b.structuralTag) || 999));
                if (pTags.length === 0) return null;
                return (
                  <div key={p} className="rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: `${PRI_COLORS[p].text}30` }}>
                    <div className="px-3 py-1.5 text-[11px] font-bold" style={{ background: PRI_COLORS[p].bg, color: PRI_COLORS[p].text, borderBottom: `1px solid ${PRI_COLORS[p].text}20` }}>
                      {PRI_COLORS[p].label} ({pTags.length} tags)
                    </div>
                    <div className="overflow-x-auto max-h-[180px] overflow-y-auto">
                      <table className="w-full text-[10px]">
                        <thead><tr style={{ background: 'var(--card-bg-alt)' }}>
                          {['Sub-Pri', 'Tag #', 'Structure', 'MTO Wt', 'Action'].map(h => (
                            <th key={h} className="px-2 py-1 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {pTags.map((t, idx) => (
                            <tr key={t.structuralTag} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td className="px-2 py-1">
                                <input type="number" min={1} value={getEffSub(t.structuralTag) || idx + 1} onChange={e => setTagPriority(t.structuralTag, p, Number(e.target.value))}
                                  className="w-10 text-center text-[10px] px-1 py-0.5 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                              </td>
                              <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{t.structuralTag}</td>
                              <td className="px-2 py-1" style={{ color: 'var(--text-muted)' }}>{t.structureNameRef}</td>
                              <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(t.totalMTOWeight)} kg</td>
                              <td className="px-2 py-1 flex gap-0.5">
                                <button onClick={() => { if (idx > 0) { const prev = pTags[idx - 1]; setTagPriority(t.structuralTag, p, getEffSub(prev.structuralTag) || idx); setTagPriority(prev.structuralTag, p, (getEffSub(t.structuralTag) || idx + 1)); } }}
                                  className="p-0.5 rounded" style={{ background: 'var(--card-bg-alt)' }}><ArrowUp className="w-3 h-3" style={{ color: 'var(--text-muted)' }} /></button>
                                <button onClick={() => { if (idx < pTags.length - 1) { const next = pTags[idx + 1]; setTagPriority(t.structuralTag, p, getEffSub(next.structuralTag) || idx + 2); setTagPriority(next.structuralTag, p, (getEffSub(t.structuralTag) || idx + 1)); } }}
                                  className="p-0.5 rounded" style={{ background: 'var(--card-bg-alt)' }}><ArrowDown className="w-3 h-3" style={{ color: 'var(--text-muted)' }} /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* STEP 4: Impact Preview */}
          {step === 4 && (
            <div className="space-y-3">
              {previewLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
              ) : preview ? (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 rounded border text-center" style={{ background: '#ef444408', borderColor: '#ef444430' }}>
                      <p className="text-[9px]" style={{ color: '#ef4444' }}>Unallocated</p>
                      <p className="text-xl font-bold" style={{ color: '#ef4444' }}>{preview.summary?.unallocated || 0}</p>
                    </div>
                    <div className="p-2 rounded border text-center" style={{ background: '#f59e0b08', borderColor: '#f59e0b30' }}>
                      <p className="text-[9px]" style={{ color: '#f59e0b' }}>Partial</p>
                      <p className="text-xl font-bold" style={{ color: '#f59e0b' }}>{preview.summary?.partial || 0}</p>
                    </div>
                    <div className="p-2 rounded border text-center" style={{ background: '#22c55e08', borderColor: '#22c55e30' }}>
                      <p className="text-[9px]" style={{ color: '#22c55e' }}>Fully Allocated</p>
                      <p className="text-xl font-bold" style={{ color: '#22c55e' }}>{preview.summary?.fullyAllocated || 0}</p>
                    </div>
                  </div>
                  <div className="rounded-lg border overflow-hidden max-h-[280px] overflow-y-auto" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                    <table className="w-full text-[10px]">
                      <thead className="sticky top-0 z-10">
                        <tr style={{ background: 'var(--card-bg-alt)' }}>
                          {['Tag', 'Priority', 'Soft %', 'Hard %', 'Status', 'MTO Wt'].map(h => (
                            <th key={h} className="px-2 py-1.5 text-left font-semibold" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(preview.changes || []).map(c => (
                          <tr key={c.structuralTag} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{c.structuralTag}</td>
                            <td className="px-2 py-1"><span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: PRI_COLORS[c.priority]?.bg, color: PRI_COLORS[c.priority]?.text }}>P{c.priority}</span></td>
                            <td className="px-2 py-1 font-mono" style={{ color: '#3b82f6' }}>{(c.softAllocPct || 0).toFixed(0)}%</td>
                            <td className="px-2 py-1 font-mono" style={{ color: '#22c55e' }}>{(c.hardAllocPct || 0).toFixed(0)}%</td>
                            <td className="px-2 py-1"><span className="text-[9px]" style={{ color: c.workfrontStatus === 'FULLY_ALLOCATED' ? '#22c55e' : c.workfrontStatus?.includes('PARTIAL') ? '#f59e0b' : '#ef4444' }}>{c.workfrontStatus?.replace(/_/g, ' ')}</span></td>
                            <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(c.totalMTOWeight)} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : <p className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>No preview data</p>}
            </div>
          )}

          {/* STEP 5: Confirm */}
          {step === 5 && (
            <div className="space-y-4 text-center py-6">
              <div className="text-4xl">&#9889;</div>
              <h4 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Ready to Apply</h4>
              <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                {Object.keys(assignments).length} priority assignments will be saved. Soft and Hard allocation will run immediately.
              </p>
              <div className="grid grid-cols-5 gap-1 max-w-md mx-auto">
                {[1,2,3,4,5].map(p => (
                  <div key={p} className="p-1.5 rounded text-center" style={{ background: PRI_COLORS[p].bg }}>
                    <p className="text-[9px]" style={{ color: PRI_COLORS[p].text }}>P{p}</p>
                    <p className="text-sm font-bold" style={{ color: PRI_COLORS[p].text }}>{priorityDist[p]?.count || 0}</p>
                  </div>
                ))}
              </div>
              <Button onClick={applyAll} disabled={applying} className="mt-4" style={{ background: '#F59206' }} data-testid="wizard-apply-btn">
                {applying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                {applying ? 'Applying...' : 'Apply & Run Allocation'}
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <Button variant="outline" size="sm" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} className="text-[10px]" data-testid="wizard-prev-btn">
            <ChevronLeft className="w-3 h-3 mr-1" /> Previous
          </Button>
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Step {step} of 5</span>
          {step < 5 ? (
            <Button size="sm" onClick={() => setStep(s => Math.min(5, s + 1))} className="text-[10px]" style={{ background: '#F59206' }} data-testid="wizard-next-btn">
              Next <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
